import { useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { delay } from '@devxdiscover/devhub-common';
import { CNAPCGAAudit } from '../../insights/types/InsightsTypes';
import { CnapBatchFetchResult } from '../types';

/** Default retry configuration for CNAP fetches */
const CNAP_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  backoffMultiplier: 1.5,
  maxDelayMs: 3000,
};

/**
 * Calculate backoff delay for retry attempts
 * @param attempt Current retry attempt number (0-indexed)
 */
function calculateBackoffDelay(attempt: number): number {
  const delayMs =
    CNAP_RETRY_CONFIG.initialDelayMs *
    Math.pow(CNAP_RETRY_CONFIG.backoffMultiplier, attempt);
  return Math.min(Math.ceil(delayMs), CNAP_RETRY_CONFIG.maxDelayMs);
}

/**
 * Custom hook for fetching CNAP data in batches with retry support
 * @returns Object with fetchCnapDataBatch function
 */
export const useCnapBatchFetch = () => {
  const config = useApi(configApiRef);

  type CnapBatchApiResponse = {
    success?: boolean;
    data?: Record<string, CNAPCGAAudit[] | undefined>;
    meta?: {
      totalRequested?: number;
      failedCount?: number;
      successCount?: number;
      failedAppIds?: number[];
    };
  };

  /**
   * Fetch CNAP data for multiple applications via batch REST API with automatic retry.
   * This consolidates many individual calls into a single backend request.
   * @param appIds Array of application IDs to fetch CNAP data for
   * @param abortSignal Optional abort signal to cancel the request
   * @returns CnapBatchFetchResult with data map and any failed app IDs
   */
  const fetchCnapDataBatch = useCallback(
    async (
      appIds: number[],
      abortSignal?: AbortSignal,
    ): Promise<CnapBatchFetchResult> => {
      const baseUrl = config.getString('app.baseUrl');
      const cnapDataMap = new Map<number, CNAPCGAAudit[]>();
      const failedAppIds: number[] = [];

      if (appIds.length === 0) {
        return { data: cnapDataMap, failedAppIds };
      }

      let lastError: Error | null = null;
      let attempt = 0;

      while (attempt <= CNAP_RETRY_CONFIG.maxRetries) {
        // Check for abort before each attempt
        if (abortSignal?.aborted) {
          return { data: cnapDataMap, failedAppIds: appIds };
        }

        try {
          const response = await axios.post(
            `${baseUrl}/api/cnap/cga-audit/batch`,
            { applicationIds: appIds },
            { signal: abortSignal },
          );

          const { success, data, meta } = response.data as CnapBatchApiResponse;
          // Treat 200 (all success) and 207 (partial success) as usable
          if (data && (success || response.status === 207)) {
            // Convert response object to Map
            Object.entries(data).forEach(([appIdStr, cnapData]) => {
              const appId = parseInt(appIdStr, 10);
              cnapDataMap.set(appId, (cnapData as CNAPCGAAudit[]) || []);
            });

            // Prefer explicit backend failure metadata when available
            if (
              Array.isArray(meta?.failedAppIds) &&
              meta.failedAppIds.length > 0
            ) {
              const requestedAppIds = new Set(appIds);
              meta.failedAppIds.forEach(appId => {
                if (requestedAppIds.has(appId)) {
                  failedAppIds.push(appId);
                }
              });
            } else {
              const failedCount = meta?.failedCount ?? 0;

              // Fallback strategy:
              // 1) Missing keys are always considered failed
              // 2) If backend indicates more failures, treat empty arrays as candidates
              const missingAppIds = appIds.filter(
                appId => !cnapDataMap.has(appId),
              );
              failedAppIds.push(...missingAppIds);

              if (failedCount > missingAppIds.length) {
                const emptyDataAppIds = appIds.filter(
                  appId =>
                    cnapDataMap.has(appId) &&
                    (cnapDataMap.get(appId)?.length ?? 0) === 0,
                );
                const remainingFailures = failedCount - missingAppIds.length;
                failedAppIds.push(
                  ...emptyDataAppIds.slice(0, remainingFailures),
                );
              }
            }

            return {
              data: cnapDataMap,
              failedAppIds: Array.from(new Set(failedAppIds)),
            };
          }

          // Response was not successful, treat as failure
          throw new Error('CNAP API returned unsuccessful response');
        } catch (err: AxiosError | unknown) {
          lastError = err instanceof Error ? err : new Error(String(err));

          // Don't retry if aborted
          if (
            abortSignal?.aborted ||
            (err instanceof DOMException && err.name === 'AbortError')
          ) {
            return { data: cnapDataMap, failedAppIds: appIds };
          }

          // Check if error is retryable (5xx, network errors, 429)
          const axiosErr = err as AxiosError;
          const status = axiosErr?.response?.status;
          const isRetryable = !status || status >= 500 || status === 429;

          if (!isRetryable || attempt >= CNAP_RETRY_CONFIG.maxRetries) {
            // eslint-disable-next-line no-console
            console.warn(
              `Failed to fetch CNAP batch data after ${attempt + 1} attempt(s):`,
              lastError.message,
            );
            return { data: cnapDataMap, failedAppIds: appIds };
          }

          // Wait before retry with exponential backoff
          const delayMs = calculateBackoffDelay(attempt);
          // eslint-disable-next-line no-console
          console.info(
            `CNAP fetch attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`,
          );
          await delay(delayMs);
          attempt++;
        }
      }

      return { data: cnapDataMap, failedAppIds: appIds };
    },
    [config],
  );

  /**
   * Retry fetching CNAP data for specific failed app IDs
   * @param failedAppIds Array of application IDs that failed previously
   * @param abortSignal Optional abort signal to cancel the request
   * @returns CnapBatchFetchResult with any recovered data
   */
  const retryCnapForFailedApps = useCallback(
    async (
      failedAppIds: number[],
      abortSignal?: AbortSignal,
    ): Promise<CnapBatchFetchResult> => {
      if (failedAppIds.length === 0) {
        return { data: new Map(), failedAppIds: [] };
      }

      // Retry in smaller batches for failed apps
      const RETRY_BATCH_SIZE = 10;
      const allData = new Map<number, CNAPCGAAudit[]>();
      const stillFailedAppIds: number[] = [];

      for (let i = 0; i < failedAppIds.length; i += RETRY_BATCH_SIZE) {
        if (abortSignal?.aborted) {
          // Add remaining as failed
          stillFailedAppIds.push(...failedAppIds.slice(i));
          break;
        }

        const batch = failedAppIds.slice(i, i + RETRY_BATCH_SIZE);
        const result = await fetchCnapDataBatch(batch, abortSignal);

        // Merge successful data
        result.data.forEach((value, key) => {
          allData.set(key, value);
        });

        // Track still-failed apps
        stillFailedAppIds.push(...result.failedAppIds);
      }

      return { data: allData, failedAppIds: stillFailedAppIds };
    },
    [fetchCnapDataBatch],
  );

  return { fetchCnapDataBatch, retryCnapForFailedApps };
};
